"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { useWorkspaceStore } from "@/store/workspace/workspace-store";
import { handleWorkspaceNotFound } from "@/utils/workspace";

/**
 * WorkspaceUrlSync
 *
 * Keeps selected workspace in sync with the URL query param.
 *
 * Canonical query param: `workspace_id`
 * Backwards-compat read: `workspace`
 */
export const WorkspaceUrlSync = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const workspaceIdParam =
    searchParams.get("workspace_id") ?? searchParams.get("workspace");

  const {
    workspaces,
    selectedWorkspace,
    setSelectedWorkspace,
    workspacesLoaded,
  } = useWorkspaceStore();

  /**
   * Sync rules:
   * 1) If URL has workspace_id (or legacy workspace) and it's in the user's workspace list, select it.
   * 2) If we have a selected workspace, ensure the URL contains `workspace_id=<selected>` (and remove `workspace`).
   *
   * Note: we intentionally re-check on navigation (searchParams/pathname changes) so links like <Link href="/">
   * still end up with a workspace_id in the URL.
   */
  useEffect(() => {
    if (!workspacesLoaded || !pathname) return;

    const selectedId = selectedWorkspace?.workspace_id ?? null;
    const canonicalInUrl = searchParams.get("workspace_id");
    const legacyInUrl = searchParams.get("workspace");
    const effectiveUrlId = canonicalInUrl ?? legacyInUrl;
  

    // If URL specifies a workspace and it differs from current selection, apply it.
    if (effectiveUrlId && effectiveUrlId !== selectedId) {
      const ok = setSelectedWorkspace({
        workspace_id: effectiveUrlId,
      });
      if (!ok) {
        handleWorkspaceNotFound();
      }
      return;
    }

    // If we have a selection, keep URL canonicalized.
    if (selectedId) {
      const shouldUpdateUrl =
        canonicalInUrl !== selectedId || searchParams.has("workspace");

      if (shouldUpdateUrl) {
        const params = new URLSearchParams(searchParams.toString());
        params.delete("workspace");
        params.set("workspace_id", selectedId);
        router.replace(`${pathname}?${params.toString()}`);
        return;
      }
    }
  }, [
    workspacesLoaded,
    pathname,
    searchParams,
    workspaceIdParam,
    workspaces,
    selectedWorkspace,
    setSelectedWorkspace,
    router,
  ]);

  return null;
};
